// The General's presence on the interface (Nick, 14.10): a small constellation on his panel that gains a star each time
// one of his counsels is taken (« Fais-le » writes it into his memory), his words that appear as if engraved, and the
// flash of a memory when he remembers (« Tu as perdu deux hommes sur Cydraar. Cette fois, prends le Lourd. »).
// The memory of the expeditions is Corthexis's (a typed note written by the world when an expedition settles), read back
// through the Archives by the preparation and the Séance.
import { signal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { useSig } from './useSig.js';
import { fetchArchives } from '../net.js';

// ── the constellation ─────────────────────────────────────────────────────────────────────────────────────────────────
/** How many counsels the player took: Corthexis's choices layer (`counsel.taken` notes, via the Archives), plus the ones
 *  taken since the last read (optimistic, the next Archives read gives the server's count). */
export const counselTaken = signal<number>(0);
/** The star just added (its flare plays once). */
const freshStar = signal<number>(-1);

export function noteCounselTaken(): void {
  const n = counselTaken.value + 1;
  counselTaken.value = n;
  freshStar.value = n - 1;
}
/** The server's count (Archives). */
export function setCounselTaken(n: number | undefined): void { if (typeof n === 'number' && n >= counselTaken.value - 1) counselTaken.value = n; }

const STARS = 9;
/** The constellation's stars for a persona: fixed points in a 120×36 box, a path that wanders left to right. */
export function constellation(seed: string): { x: number; y: number }[] {
  let h = 2166136261;
  for (const c of seed) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  const rnd = (): number => { h = (Math.imul(h, 1664525) + 1013904223) >>> 0; return h / 4294967296; };
  return Array.from({ length: STARS }, (_, i) => ({ x: 6 + (i * 108) / (STARS - 1) + (rnd() - 0.5) * 6, y: 6 + rnd() * 24 }));
}

/** The constellation of counsels taken: lit stars joined by lines; past STARS, a new one starts (the count is shown). */
export function Constellation({ persona }: { persona: string }) {
  const n = useSig(counselTaken), fresh = useSig(freshStar);
  const pts = constellation(persona);
  const lit = n === 0 ? 0 : ((n - 1) % STARS) + 1, round = Math.floor(Math.max(0, n - 1) / STARS);
  const newest = fresh >= 0 && fresh === n - 1 ? lit - 1 : -1;
  useEffect(() => { if (newest >= 0) { const t = setTimeout(() => { freshStar.value = -1; }, 2600); return () => clearTimeout(t); } return undefined; }, [newest, n]);
  return (
    <span class="constellation" title={`${n}`} aria-label={`${n}`}>
      <svg viewBox="0 0 120 36" width="120" height="36" aria-hidden="true">
        {pts.slice(1).map((p, i) => {
          const a = pts[i]!, on = i + 1 < lit;
          const len = Math.hypot(p.x - a.x, p.y - a.y);
          return <line key={`l${i}`} x1={a.x} y1={a.y} x2={p.x} y2={p.y} class={on ? `on ${i + 1 === newest ? 'new' : ''}` : ''} style={{ strokeDasharray: len, strokeDashoffset: 0, ['--len' as string]: len }} />;
        })}
        {pts.map((p, i) => <circle key={`s${i}`} cx={p.x} cy={p.y} r={i < lit ? 2.3 : 1.3} class={i < lit ? `on ${i === newest ? 'new' : ''}` : ''} />)}
        {newest >= 0 && <circle cx={pts[newest]!.x} cy={pts[newest]!.y} r={3} class="burst" />}
      </svg>
      {round > 0 && <b>×{round + 1}</b>}
    </span>
  );
}

// ── engraved words ────────────────────────────────────────────────────────────────────────────────────────────────────
/** Words that appear one after another, as if cut into stone (a light runs over each, then it settles). */
export function Engraved({ text, step = 70, delay = 0, play = true }: { text: string; step?: number; delay?: number; play?: boolean }) {
  if (!play) return <>{text}</>;
  const words = text.split(/(\s+)/);
  let k = 0;
  return <span class="engraved">{words.map((w, i) => (/^\s+$/.test(w) ? w : <span key={i} class="w" style={{ animationDelay: `${delay + step * k++}ms` }}>{w}</span>))}</span>;
}

// ── the memory of the expeditions (Corthexis, server side) ────────────────────────────────────────────────────────────
// The world writes one typed note per expedition settled (apps/world/src/xpmemory.ts); the Archives return them.
export interface XpMemory { system: string; name: string; lost: number; home: number; won: boolean; preset: 'light' | 'standard' | 'heavy' | null; at: number }
/** The expeditions the General remembers, as the last Archives said (newest first). */
export const xpMemory = signal<XpMemory[]>([]);
export async function refreshXpMemory(): Promise<void> { const a = await fetchArchives(); if (a?.expeditions) xpMemory.value = a.expeditions; setCounselTaken(a?.counselTaken); }

const NUM_FR = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix'];
const NUM_EN = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const KIT = { fr: { light: 'le Léger', standard: 'le Standard', heavy: 'le Lourd' }, en: { light: 'the Light kit', standard: 'the Standard kit', heavy: 'the Heavy kit' } } as const;

const costlyOf = (mem: readonly XpMemory[], here?: string): XpMemory | undefined => { const c = mem.filter((m) => m.lost > 0); return c.find((x) => x.system === here) ?? c[0]; };
/** The kit the memory asks for: one step heavier than the one that cost men (null: already the heaviest, or no memory). */
export function recallKit(mem: readonly XpMemory[], here?: string): 'standard' | 'heavy' | null {
  const m = costlyOf(mem, here);
  if (!m) return null;
  return m.preset === 'heavy' ? null : m.preset === 'light' ? 'standard' : 'heavy';
}

/** What the General says when he remembers a costly expedition, or null (none with losses). `here`: the wreck now aimed
 *  at, to prefer a memory of the same system. `affordable` false: the capital cannot pay the kit he would ask for, so he
 *  does not ask for it (he never advises what cannot be done). */
export function recallLine(mem: readonly XpMemory[], lang: 'fr' | 'en', here?: string, affordable = true): string | null {
  const m = costlyOf(mem, here);
  if (!m) return null;
  const n = m.lost <= 10 ? (lang === 'fr' ? NUM_FR : NUM_EN)[m.lost]! : String(m.lost);
  const next = recallKit(mem, here);
  if (lang === 'fr') {
    const lost = `Tu as perdu ${n} ${m.lost > 1 ? 'hommes' : 'homme'} sur ${m.name}.`;
    if (!next) return `${lost} Cette fois, ne cours pas : tiens la baie.`;
    return affordable ? `${lost} Cette fois, prends ${KIT.fr[next]}.` : `${lost} Il faudrait ${KIT.fr[next]}, la capitale ne le paie pas : tiens la baie, ne cours pas.`;
  }
  const lost = `You lost ${n} ${m.lost > 1 ? 'men' : 'man'} on ${m.name}.`;
  if (!next) return `${lost} This time, do not run: hold the bay.`;
  return affordable ? `${lost} This time, take ${KIT.en[next]}.` : `${lost} It would take ${KIT.en[next]}, the capital cannot pay it: hold the bay, do not run.`;
}

/** The flash of a memory: a golden shard bursts, then the General's line is engraved under « Il se souvient ». */
export function RecallFlash({ label, text }: { label: string; text: string }) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => { host.current?.classList.remove('play'); void host.current?.offsetWidth; host.current?.classList.add('play'); }, [text]);
  return (
    <div class="recall play" ref={host} role="note" data-testid="general-recall">
      <span class="shard" aria-hidden="true">{Array.from({ length: 10 }, (_, i) => <i key={i} style={{ ['--a' as string]: `${i * 36 + 8}deg`, ['--d' as string]: `${(i % 3) * 60}ms` }} />)}</span>
      <small>{label}</small>
      <p><Engraved text={text} delay={520} step={85} /></p>
    </div>
  );
}
